/**
 * CaseOfferPanel — Phase 4 Offer portal (G.12/G.13). Generate the client-facing offer email
 * (Lloyd's wording + Ken's learn-more link) and, on acceptance, the drafted court acceptance for
 * staff to review and send. Both are drafts (content shown to copy); nothing auto-sends.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OfferPortalApi, { OfferType, DraftedEmail } from '../../api/OfferPortalApi';

interface CaseOfferPanelProps {
  Ticket_guid: string;
}

const OFFER_OPTIONS: { value: OfferType; label: string }[] = [
  { value: 'dsc', label: 'Driver Safety Course (DSC)' },
  { value: 'deferral', label: 'Deferral / deferred disposition' },
  { value: 'conviction', label: 'Conviction (neither DSC nor deferral)' },
  { value: 'appeal', label: 'Appeal / jury trial' },
];

const CaseOfferPanel: React.FC<CaseOfferPanelProps> = ({ Ticket_guid }) => {
  const [offerType, setOfferType] = useState<OfferType>('deferral');
  const [deferralDays, setDeferralDays] = useState<string>('');
  const [deferralFee, setDeferralFee] = useState<string>('');
  const [dscAlso, setDscAlso] = useState(false);
  const [clientDraft, setClientDraft] = useState<DraftedEmail | null>(null);
  const [courtDraft, setCourtDraft] = useState<DraftedEmail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const genClient = async () => {
    setError(null);
    try {
      const draft = await OfferPortalApi.buildOfferEmail(Ticket_guid, offerType, {
        deferralDays: deferralDays ? Number(deferralDays) : null,
        deferralFeeUsd: deferralFee ? Number(deferralFee) : null,
        dscAlsoRequired: dscAlso,
      });
      setClientDraft(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build offer email');
    }
  };

  const genCourt = async () => {
    setError(null);
    try {
      setCourtDraft(await OfferPortalApi.buildCourtAcceptance(Ticket_guid, offerType));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build court acceptance draft');
    }
  };

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      setError('Could not copy.');
    }
  };

  const renderDraft = (title: string, draft: DraftedEmail) => (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={() => copy(title, `${draft.subject}\n\n${draft.body}`)}>
          Copy
        </Button>
      </Stack>
      <TextField label="Subject" value={draft.subject} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 1 }} />
      <TextField label="Body" value={draft.body} fullWidth multiline minRows={8} InputProps={{ readOnly: true }} />
      {draft.learnMoreUrl && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          Learn-more link: <MuiLink href={draft.learnMoreUrl} target="_blank" rel="noopener">{draft.learnMoreUrl}</MuiLink>
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Offer emails
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
        <TextField
          select
          label="Offer type"
          value={offerType}
          onChange={(e) => setOfferType(e.target.value as OfferType)}
          size="small"
          sx={{ minWidth: 260 }}
        >
          {OFFER_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        {offerType === 'deferral' && (
          <>
            <TextField label="Deferral days" type="number" size="small" value={deferralDays} onChange={(e) => setDeferralDays(e.target.value)} sx={{ maxWidth: 140 }} />
            <TextField label="Deferral fee (USD)" type="number" size="small" value={deferralFee} onChange={(e) => setDeferralFee(e.target.value)} sx={{ maxWidth: 160 }} />
            <FormControlLabel control={<Checkbox checked={dscAlso} onChange={(e) => setDscAlso(e.target.checked)} />} label="DSC also required" />
          </>
        )}
      </Stack>
      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={genClient}>Generate client email</Button>
        <Button variant="outlined" onClick={genCourt}>Draft court acceptance</Button>
      </Stack>

      {copied && <Alert severity="success" sx={{ mt: 2 }}>{copied} copied.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {clientDraft && renderDraft('Client offer email', clientDraft)}
      {courtDraft && (
        <>
          <Divider sx={{ my: 2 }} />
          {renderDraft('Court acceptance draft', courtDraft)}
        </>
      )}
    </Box>
  );
};

export default CaseOfferPanel;
